/**
 * components/Bar/BarRow
 * One row of "Who holds the most": the holder's name and their amount on a
 * line, and a 7px bar beneath, scaled to the LARGEST holder rather than to the
 * total. Scaling to the largest is what makes the comparison between holders
 * legible — scaled to the total, five holders of similar size are five short
 * stubs.
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize } from '../../theme/typography';
import Money from '../Money';

export interface BarRowProps {
  label: string;
  /** Secondary line — the holder type. */
  meta?: string;
  /** Minor units. */
  value: string;
  cur: string;
  /** The largest value in the set, in the same currency. */
  max: string;
  color?: string;
}

export function BarRow({ label, meta, value, cur, max, color = neutral.ink }: BarRowProps) {
  // Ratio in BigInt-safe terms: both are minor-unit strings, and Number() on a
  // ratio is safe where Number() on the values themselves would not be.
  const numerator = Number(value);
  const denominator = Number(max);
  const percent =
    Number.isFinite(numerator) && Number.isFinite(denominator) && denominator > 0
      ? Math.max(2, Math.min(100, (numerator / denominator) * 100))
      : 0;

  return (
    <View style={styles.row}>
      <View style={styles.line}>
        <Text numberOfLines={1} style={styles.label}>
          {label}
          {!!meta && <Text style={styles.meta}> · {meta}</Text>}
        </Text>
        <Money value={value} cur={cur} size={fontSize.label} align="right" />
      </View>
      <View style={styles.track}>
        <View style={[styles.fill, { width: `${percent}%`, backgroundColor: color }]} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { gap: 5 },
  line: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 },
  label: { ...sans('500'), fontSize: fontSize.label, color: neutral.ink, flex: 1, minWidth: 0 },
  meta: { ...sans('400'), color: neutral.grey400 },
  track: { height: 7, borderRadius: radii.pill, backgroundColor: neutral.ruleLight, overflow: 'hidden' },
  fill: { height: '100%' },
});

export default BarRow;
